import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Clock, AlertCircle, ExternalLink, MessageCircle, X } from 'lucide-react';
import { expiraEmFormat, timeAgo } from '../utils/feedHelpers';
import { openWhatsApp } from '../utils/whatsappLink';
import BackendImage from './BackendImage';

/**
 * FeedCard - card de um post do Feed.
 *
 * Props:
 *  - post: { id, negocio_id, negocio_nome, negocio_foto, negocio_categoria, negocio_nivel_irn,
 *            categoria_post, label_categoria?, texto, foto_url, expira_em, criado_em }
 *  - variant: "full" | "compact" (compact = sem botões, texto truncado)
 *  - business: opcional {whatsapp, slug, bairro} para enriquecer o card (passado pelo widget de negócio)
 *  - onShare / onView: handlers opcionais
 */
export default function FeedCard({ post, variant = 'full', business = null, categoriasMap = {} }) {
  const [lightboxOpen, setLightboxOpen] = useState(false);

  useEffect(() => {
    if (!lightboxOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') setLightboxOpen(false); };
    document.addEventListener('keydown', onKey);
    // Trava o scroll do body enquanto o lightbox estiver aberto
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [lightboxOpen]);

  if (!post) return null;
  const timer = expiraEmFormat(post.expira_em);
  const label = categoriasMap[post.categoria_post] || post.categoria_post;
  const isCompact = variant === 'compact';
  const texto = isCompact && post.texto?.length > 80 ? `${post.texto.slice(0, 80).trim()}…` : post.texto;
  const ts = timeAgo(post.criado_em);

  // negocio_foto pode vir como string URL OU como objeto {thumb, medium, large}
  const fotoRaw = post.negocio_foto;
  const fotoSrc = typeof fotoRaw === 'string'
    ? fotoRaw
    : (fotoRaw?.thumb || fotoRaw?.medium || fotoRaw?.large || fotoRaw?.url || null);

  // Whatsapp/slug: pode vir 1) embutido no post (preferido), 2) via prop business
  const whatsapp = post.negocio_whatsapp || business?.whatsapp;
  const slug = post.negocio_slug || business?.slug;
  const hasWhatsapp = !!whatsapp;
  // Rota real de detalhe do negócio é `/negocio/:businessSlug` (singular) — ver App.js.
  const perfilLink = slug ? `/negocio/${slug}` : `/negocio/${post.negocio_id}`;

  const handleWhatsApp = (e) => {
    e.preventDefault();
    e.stopPropagation();
    openWhatsApp({
      business: {
        id: post.negocio_id,
        nome: post.negocio_nome,
        whatsapp,
        categoria: post.negocio_categoria,
        municipio_slug: business?.municipio_slug || post.negocio_municipio_slug,
        municipio_nome: business?.municipio_nome,
      },
      municipio: business?.municipio || null,
    });
  };

  return (
    <article
      data-testid={`feed-card-${post.id}`}
      className={`bg-white rounded-2xl border border-gray-100 hover:border-purple-200 shadow-sm hover:shadow-md transition overflow-hidden ${isCompact ? 'p-3' : 'p-4 md:p-5'}`}
    >
      {/* header: avatar + nome + nivel IRN */}
      <header className="flex items-start gap-3">
        {fotoSrc ? (
          <BackendImage
            src={fotoSrc}
            alt={post.negocio_nome}
            className={`${isCompact ? 'w-9 h-9' : 'w-10 h-10'} rounded-full object-cover bg-gray-100 shrink-0`}
          />
        ) : (
          <div className={`${isCompact ? 'w-9 h-9' : 'w-10 h-10'} rounded-full bg-purple-100 text-purple-700 font-bold flex items-center justify-center shrink-0`}>
            {(post.negocio_nome || '?').charAt(0).toUpperCase()}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <Link
              to={perfilLink}
              data-testid={`feed-card-${post.id}-negocio-link`}
              className={`font-semibold truncate hover:text-purple-700 ${isCompact ? 'text-sm' : 'text-base'}`}
            >
              {post.negocio_nome || 'Negócio'}
            </Link>
            {post.negocio_nivel_irn > 0 && (
              <span
                className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-amber-100 text-amber-800"
                data-testid={`feed-card-${post.id}-nivel`}
                title={`Nível ${post.negocio_nivel_irn}`}
              >
                Nv {post.negocio_nivel_irn}
              </span>
            )}
          </div>
          <div className={`text-xs text-gray-500 truncate ${isCompact ? '' : 'mt-0.5'}`}>
            {post.negocio_categoria}
            {business?.bairro && ` · ${business.bairro}`}
            {!isCompact && ts && <span className="text-gray-400"> · {ts}</span>}
          </div>
        </div>
      </header>

      {/* faixa: badge categoria_post + timer */}
      <div className={`flex items-center justify-between gap-2 ${isCompact ? 'mt-2' : 'mt-3'}`}>
        <span
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 font-medium ${isCompact ? 'text-[10px]' : 'text-xs'}`}
          data-testid={`feed-card-${post.id}-categoria-badge`}
        >
          {label}
        </span>
        <span className={`inline-flex items-center gap-1 ${timer.cor} ${isCompact ? 'text-[10px]' : 'text-xs'}`}>
          {timer.urgente ? <AlertCircle className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
          {timer.label}
        </span>
      </div>

      {/* texto */}
      <p
        className={`leading-snug whitespace-pre-line ${isCompact ? 'text-sm mt-1.5 text-gray-700' : 'text-base mt-3 text-gray-900'}`}
        data-testid={`feed-card-${post.id}-texto`}
      >
        {texto}
      </p>

      {/* foto — clicável para abrir em tela cheia */}
      {!isCompact && post.foto_url && (
        <button
          type="button"
          onClick={() => setLightboxOpen(true)}
          className="mt-3 block w-full group focus:outline-none focus:ring-2 focus:ring-purple-400 rounded-lg"
          data-testid={`feed-card-${post.id}-foto`}
          aria-label="Ver imagem em tela cheia"
        >
          <BackendImage
            src={post.foto_url}
            alt=""
            className="w-full max-h-[200px] object-cover rounded-lg border border-gray-100 cursor-zoom-in group-hover:opacity-95 transition"
          />
        </button>
      )}

      {/* actions */}
      {!isCompact && (
        <footer className="flex items-center gap-2 mt-4">
          <Link
            to={perfilLink}
            className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-purple-50 text-purple-700 hover:bg-purple-100 transition"
            data-testid={`feed-card-${post.id}-ver-perfil`}
          >
            Ver perfil <ExternalLink className="w-3.5 h-3.5" />
          </Link>
          {hasWhatsapp && (
            <button
              type="button"
              onClick={handleWhatsApp}
              className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-green-600 text-white hover:bg-green-700 transition"
              data-testid={`feed-card-${post.id}-whatsapp`}
            >
              <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
            </button>
          )}
        </footer>
      )}

      {/* lightbox — imagem em tela cheia */}
      {lightboxOpen && post.foto_url && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={() => setLightboxOpen(false)}
          className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in"
          data-testid={`feed-card-${post.id}-lightbox`}
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setLightboxOpen(false); }}
            className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition"
            aria-label="Fechar"
            data-testid={`feed-card-${post.id}-lightbox-close`}
          >
            <X className="w-6 h-6" />
          </button>
          <BackendImage
            src={post.foto_url}
            alt=""
            onClick={(e) => e.stopPropagation()}
            className="max-w-full max-h-full object-contain rounded-lg shadow-2xl cursor-default"
          />
        </div>
      )}
    </article>
  );
}
